/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import Notify from "@eisinc/App/Components/Notify/Notify";
import ListingUploadedFiles from "@eisinc/App/Components/UploadFilesModal/ListingUploadedFiles";
import Button, { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import { modalButtonsActionStyles } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import FileUpload, { FileUploadPresentationProps } from "@insite/mobius/FileUpload/FileUpload";
import Modal from "@insite/mobius/Modal";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface UploadFilesModalProps {
    openModal: boolean;
    headline?: string;
    handleClose: () => void;
    handleContinue: (files: any) => void;
    filesNumber?: number;
    maxFilesNumberMessage?: any;
}

interface RefProps {
    resetFields?: any;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        customSettings: getSettingsCollection(state).customSettings,
    };
};

const mapDispatchToProps = {};

type Props = UploadFilesModalProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface UploadFilesModalStyles {
    fileUploader?: FileUploadPresentationProps;
    notifiesWrapper?: InjectableCss;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    continueButton?: ButtonPresentationProps;
}

export const uploadFilesModalStyles: UploadFilesModalStyles = {
    fileUploader: {
        buttonProps: {
            variant: "secondary",
        },
    },
    notifiesWrapper: {
        css: css`
            margin-bottom: 10px;

            > div {
                margin-bottom: 10px;

                &:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
    buttonsWrapper: {
        css: css`
            margin-top: 16px;
            ${modalButtonsActionStyles.buttonsGridItem?.css}
        `,
    },
    cancelButton: { ...modalButtonsActionStyles.cancelButton },
    continueButton: { ...modalButtonsActionStyles.submitButton },
};

const styles = uploadFilesModalStyles;

const UploadFilesModal = forwardRef<RefProps, Props>(
    ({ customSettings, openModal, headline, handleClose, handleContinue, filesNumber, maxFilesNumberMessage }, ref) => {
        const [files, setFiles] = useState("");
        const [uploadedFiles, setUploadedFiles] = useState([] as any);
        const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState("" as any);
        const [exceedsFileSize, setExceedsFileSize] = useState(false);
        const [fileTypeError, setFileTypeError] = useState(false);

        const fileUploadRef = useRef({ value: "" } as HTMLInputElement);

        useEffect(() => {
            setExceedsFileSize(false);
            setFileTypeError(false);
            setFiles("");
            handleFilesNamesChanges();
            validateFiles();
        }, [uploadedFiles]);

        useEffect(() => {
            handleFilesNamesChanges();
        }, [files]);

        const validateFiles = () => {
            if (!uploadedFiles.length) {
                return;
            }

            let totalSize = 0;
            let typeError = false;

            const maxFilesSize = (customSettings as any).maximumFileSize;
            const acceptedFileTypes = (customSettings as any).listOfAcceptedFileExtensions;

            for (let i = 0; i < uploadedFiles.length; i++) {
                const file = uploadedFiles[i];

                totalSize += file.size;

                const fileNameParts = file.name.split(".");
                const fileExt = fileNameParts[fileNameParts.length - 1]?.toLowerCase();

                if (acceptedFileTypes.indexOf(fileExt) < 0) {
                    typeError = true;

                    break;
                }
            }

            if (typeError) {
                setFileTypeError(true);
            }

            if (totalSize > maxFilesSize) {
                setExceedsFileSize(true);
            }
        };

        const resetFields = () => {
            setUploadedFiles([]);
            setFiles("");
            setFileUploadErrorMessage("");
            setExceedsFileSize(false);
            setFileTypeError(false);
        };

        const handleModalClose = () => {
            resetFields();
            handleClose && handleClose();
        };

        const handleFilesNamesChanges = () => {
            filesNumber = filesNumber || 3;
            let filesNames = "";

            for (let i = 0; i < uploadedFiles.length; i++) {
                filesNames += `${uploadedFiles[i].name}`;

                if (i < filesNumber - 1) {
                    filesNames += `, `;
                }
            }

            setFiles(filesNames);
        };

        const fileChangeHandler = (event: ChangeEvent<any>) => {
            if (event.target.files[0] === null) {
                resetFields();

                return;
            }

            filesNumber = filesNumber || 3;

            if (uploadedFiles.length >= filesNumber || event.target.files.length > filesNumber) {
                setFileUploadErrorMessage(maxFilesNumberMessage || siteMessage("Custom_MaxUploadedDocumentsIsThree"));
            }

            if (event.target.files.length && uploadedFiles.length < filesNumber) {
                const uploadedFilesNew = event.target.files;
                const filesToUpload = Object.assign([], uploadedFiles);

                for (let i = 0; i < filesNumber; i++) {
                    const file = uploadedFilesNew[i];

                    if (file) {
                        filesToUpload.push(file);
                    }
                }

                setUploadedFiles(filesToUpload);

                return;
            }

            setFiles("");
        };

        const handleContinueClick = () => {
            handleContinue(uploadedFiles);
        };

        const clearFromHolder = (index: number) => {
            uploadedFiles.splice(index, 1);
            setUploadedFiles([...uploadedFiles]);

            if (!uploadedFiles.length) {
                handleModalClose();
            }
        };

        useImperativeHandle(ref, () => ({ resetFields }));

        return (
            <StyledWrapper>
                <ListingUploadedFiles files={uploadedFiles} clearFromHolder={(index: any) => clearFromHolder(index)} />
                <Modal headline={headline} isOpen={openModal} handleClose={handleModalClose}>
                    {(exceedsFileSize || fileTypeError || fileUploadErrorMessage) && (
                        <StyledWrapper {...styles.notifiesWrapper}>
                            {exceedsFileSize && (
                                <Notify
                                    notifyContainerOverrides={css`
                                        max-width: 100%;
                                    `}
                                    messageType="danger"
                                    body={siteMessage("Custom_Max_File_Size_exceeded")}
                                />
                            )}
                            {fileTypeError && (
                                <Notify
                                    notifyContainerOverrides={css`
                                        max-width: 100%;
                                    `}
                                    messageType="danger"
                                    body={siteMessage("Custom_File_Type_Error")}
                                />
                            )}
                            {fileUploadErrorMessage && (
                                <Notify
                                    notifyContainerOverrides={css`
                                        max-width: 100%;
                                    `}
                                    messageType="warning"
                                    body={fileUploadErrorMessage}
                                />
                            )}
                        </StyledWrapper>
                    )}
                    <FileUpload
                        {...styles.fileUploader}
                        inputRef={fileUploadRef}
                        fileName={files}
                        label={translate("")}
                        labelPosition="top"
                        onFileChange={fileChangeHandler}
                        multiple
                        accept={(customSettings as any).listOfAcceptedFileExtensions}
                    />
                    {(exceedsFileSize || fileTypeError) && (
                        <ListingUploadedFiles
                            files={uploadedFiles}
                            clearFromHolder={(index: any) => clearFromHolder(index)}
                        />
                    )}
                    <StyledWrapper {...styles.buttonsWrapper} className="modal-actions">
                        <Button onClick={handleModalClose} {...styles.cancelButton}>
                            {translate("Cancel")}
                        </Button>
                        <Button
                            onClick={handleContinueClick}
                            disabled={exceedsFileSize || fileTypeError}
                            {...styles.continueButton}
                        >
                            {translate("Continue")}
                        </Button>
                    </StyledWrapper>
                </Modal>
            </StyledWrapper>
        );
    },
);

UploadFilesModal.displayName = "UploadFilesModal";

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(UploadFilesModal);
